// --------------------------
// IMPORTS
// --------------------------
import { useEffect, useState } from "react";
import { Input, Form, InputNumber, Space, Tooltip, Tag } from "antd";
import { useDispatch, useSelector } from "react-redux";
import TableContent from "../../components/atoms/TableContent";
import TopContainerHeader from "../../components/molecules/TopComponentHeader";
import useCurrentPath, {
  deFaultHandlePaginationChange,
  formateDateTime,
  handleDelete,
} from "../../utils/Constants";
import {
  getCurrencyListThunk,
  deleteCurrencyThunk,
  addCurrencyThunk,
  editCurrencyThunk,
} from "../../store/thunks/currenciesThunk";
import media from "../../media";
import EditForm from "../../components/molecules/Forms/editForm";
import { selectedRowInfo } from "../../store/slices/authSlice";
import dayjs from "dayjs";
import { CurrencyRateHistoryList } from "./CurrencyRateHistoryList";
import moment from "moment";

// --------------------------
// REUSABLE CELL COMPONENT
// --------------------------
const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode = inputType === "number" ? <InputNumber /> : <Input />;
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{
            margin: 0,
          }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

// --------------------------
// MAIN COMPONENT
// --------------------------
export default function Currency() {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();
  const currentPath = useCurrentPath();
  const [tableForm] = Form.useForm();

  // --------------------------
  // REDUX STATE
  // --------------------------
  const storeRole = useSelector((state) => state.auth.role);
  const storeLIUId = useSelector((state) => state.auth.lIUId);
  const currencies = useSelector((state) => state.currencies.list) || [];
  const currenciesLoading = useSelector((state) => state.currencies.loading);
  const storePageInfo = useSelector((state) => state.currencies.pageInfo);

  // --------------------------
  // LOCAL STATES
  // --------------------------
  const [showLoader, setShowLoader] = useState(true);
  const [storeTotalCount, setStoreTotalCount] = useState(0);  
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);  
  const [filteredCurrencies, setFilteredCurrencies] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [values, setValues] = useState({
    code: "",
    symbol_native: "",
    currencyRateHistory: "",
    history: "",
    status: "",
    createdAt: "",
    updatedAt: "",
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [editingKey, setEditingKey] = useState("");
  const isEditing = (record) => record.key === editingKey;

  // --------------------------
  // API CALLS
  // --------------------------

  // Fetch currencies
  const fetchCurrencies = async () => {    
    const res = await dispatch(
      getCurrencyListThunk({
        route: currentPath,
        pagination: {
          current: 1,
          pageSize: process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE,
        },
      })
    ).unwrap();

    setStoreTotalCount(res.totalCount || 0);
    setShowLoader(false);
  };

  // --------------------------
  // USE EFFECTS
  // --------------------------
  useEffect(() => {
    fetchCurrencies();
  }, [currentPath, dispatch]);

  useEffect(() => {
    setFilteredCurrencies(currencies); // sync on data change
  }, [currencies]);

  // --------------------------
  // HANDLERS
  // --------------------------
  const openEditModal = (record) => {
    setEditingCurrency(record); // store the record to edit

    tableForm.setFieldsValue({
      code: record.code,
      symbol_native: record.symbol_native,
      currencyRateHistory: record.currencyRateHistory,
    });
    setIsEditModalOpen(true); // open the modal
  };

  // --------------------------
  // TABLE COLUMNS
  // --------------------------
  const columns = [
    {
      title: "Sr. No.",
      render: (text, record, index) => index + 1,
    },

    {
      title: "Currency Code",
      dataIndex: "code",
      editable: true,
      render: (text) => <>{text}</>,
      sorter: (a, b) => a.name.localeCompare(b.code),
      render: (text, record) => (
        <span className="currencySpan">
          {record.flagUrl && (
            <img
              className="currency-flag"
              src={record.flagUrl}
              alt={record.code}
              style={{ borderRadius: "2px" }}
            />
          )}
          <span>{record.code}</span>
        </span>
      ),
    },
    {
      title: "Currency Symbol",
      dataIndex: "symbol_native",
      editable: true,
    },
    {
      title: "Currency Rate History",
      dataIndex: "currencyRateHistory",
      editable: true,
      render: (historyArray = []) => {
        if (!Array.isArray(historyArray) || historyArray.length === 0)
          return "—";

        const sortedHistory = [...historyArray].sort(
          (a, b) =>
            new Date(b.effectiveDateTime) - new Date(a.effectiveDateTime)
        );

        const latest = sortedHistory[0];

        // 🔹 ADMIN → show only recent rate
        if (storeRole === process.env.REACT_APP_ADROLE) {
          return (
            <>
              <div>
                <strong>Rate:</strong> {latest.rate}
              </div>
              <span>
                <strong>Effective:</strong>{" "}
                {dayjs(latest.effectiveDateTime).format("DD-MM-YY hh:mm A")}
              </span>
            </>
          );
        }
        
        if (storeRole === process.env.REACT_APP_SROLE) {
          // Build vertical content for tooltip
          const tooltipContent = (
            <div className="popRateDetailsWrap">
              {historyArray.map((item) => (
                <Tag
                  key={item._id}
                  color="blue"
                  style={{
                    whiteSpace: "normal",
                    textAlign: "left",
                    fontSize: "13px",
                    lineHeight: "1.4",
                    padding: "6px 10px",
                    borderRadius: "6px",
                  }}
                >
                  <div className="popRateDetails">
                    <div>
                      <strong>Rate:</strong> {item.rate}
                    </div>
                    <div>
                      <strong>Effective Date & Time:</strong>{" "}
                      {dayjs(item.effectiveDateTime).format("DD-MM-YY hh:mm A")}
                    </div>
                  </div>
                </Tag>
              ))}
            </div>
          );

          return (
            <Tooltip
              placement="topLeft"
              title={tooltipContent}
              color="white"
              overlayInnerStyle={{
                color: "#333",
                maxWidth: "320px",
                padding: "8px 12px",
              }}
            >
              <span className="table-round-btn">
                <span className="text-size-12 dark-black-color">View</span>
              </span>
            </Tooltip>
          );
        }
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      editable: true,
      className: "all-nowrap-column",
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (text) => (
        <>
          <span className={`${text?.toLowerCase()}Status`}>{text}</span>
        </>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      render: (text) => text && formateDateTime(text),
    },

    ...(storeRole === process.env.REACT_APP_SROLE ||
    storeRole === process.env.REACT_APP_ADROLE
      ? [
          {
            title: "Updated At",
            dataIndex: "updatedAt",
            render: (text) => text && formateDateTime(text),
          },
          {
            title: "Action",
            dataIndex: "action",
            render: (_, record) => (
              <span
                className="table-round-btn"
                onClick={() => openEditModal(record)}
              >
                <img src={media.editIcon.svg} alt="Edit icon" />
                <span className="text-size-12 dark-black-color">Edit</span>
              </span>
            ),
          },
        ]
      : []),
  ];

  const mergedColumns =
    columns &&
    columns.map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record) => ({
          record,
          inputType: "text",
          dataIndex: col.dataIndex,
          title: col.title,
          editing: isEditing(record),
        }),
      };
    });

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
      const selectedRowKeysChildInfoArr = selectedRows.map((row) => row.key);

      dispatch(
        selectedRowInfo({
          selectedRowKeysChildInfo: selectedRowKeysChildInfoArr,
        })
      );
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      name: record.name,
    }),
  };

  // --------------------------
  // FORM FIELDS
  // --------------------------
  const formFields = [
    {
      isVisible: true,
      label: "Currency Code",
      name: "code",
      rules: [
        {
          required: true,
          message: "Please select Currency code",
        },
      ],
      placeholder: "Enter the currency code",
      type: "input",
      onChange: (e) => {
        setValues({
          ...values,
          code: e.target.value,
        });
      },
    },
    {
      isVisible: true,
      label: "Currency Symbol",
      name: "symbol_native",
      rules: [
        {
          required: true,
          message: "Please select Currency Symbol",
        },
      ],
      placeholder: "Enter the currency symbol",
      type: "input",
      onChange: (e) => {
        setValues({
          ...values,
          symbol_native: e.target.value,
        });
      },
    },
    {
      isVisible: true,
      label: "Currency Rate",
      name: "rate",
      rules: [
        { required: true, message: "Please input rate!" },
        {
          pattern: /^\d+(\.\d{1,2})?$/,
          message: "Enter a currency rate (up to 2 decimal places)",
        },
      ],
      placeholder: "Enter the currency rate",
      type: "input",
      onChange: (e) => {
        setValues({
          ...values,
          code: e.target.value,
        });
      },
    },
    {
      isVisible: true,
      label: "Effective Date & Time",
      name: "effectiveDateTime",
      rules: [{ required: true, message: "Please select date & time" }],
      type: "datepicker",
      picker: "date",
      showTime: true,
      format: "YYYY-MM-DD HH:mm",
      disabledDate: (current) => {        
        return storeRole !== process.env.REACT_APP_SROLE && current && current < moment().startOf("month");
      },
      onChange: (date, dateString) => {
        setValues({
          ...values,
          effectiveDateTime: dateString,
        });
      },
    },
  ];

  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <div className="container">
      <div className="bgi-no-repeat card-stretch gutter-b rounded-lg p-0">
        <TopContainerHeader
          sectionNameClass="col-lg-7 text-left commonTitleText"
          sectionTitle="Currency"
          sectionDescription={
            showLoader ? "Loading..." : `${storeTotalCount} Found`
          }
          hasSectionButton={false}
          hasSearch={true}
          hasSearchButton="general"
          addNewButtonClassName="btn mx-auto px-5 py-2 defaultbutton rounded-lg"
          name={
            storeRole === process.env.REACT_APP_SROLE ||
            storeRole === process.env.REACT_APP_ADROLE
              ? "Add New Currency"
              : ""
          }
          searchClassName="table-search"
          searchPlaceholder="Search Currency's Code"
          onButtonClick={() => setIsAddModalOpen(true)}
          searchData={currencies}
          onFilteredData={setFilteredCurrencies}
          searchKeys={["code"]}
          addFormModalOpen={isAddModalOpen}
          addFormSetIsModalOpen={setIsAddModalOpen}
          addFormModalShowLoader={setShowLoader}
          payload={(values) => ({
            route: currentPath,
            code: values?.code,
            symbol_native: values?.symbol_native,
            rate: values?.rate,
            effectiveDateTime: dayjs(values?.effectiveDateTime).format(
              "YYYY-MM-DD HH:mm"
            ),
            createdAt: new Date().toISOString(),
            historySource: "backend",
            historyAction: "create",
            historyUserID: storeLIUId,
          })}
          list={addCurrencyThunk}
          formFields={formFields}
          successMessage="Currency added successfully."
          onSuccess={fetchCurrencies}
          selectedRowKeys={
            storeRole === process.env.REACT_APP_SROLE && selectedRowKeys
          }
          deleteOnClick={() => {
            handleDelete({
              selectedRowKeys,
              dispatch,
              deleteThunk: deleteCurrencyThunk,
              route: currentPath,
              actionName: "currency",
              onSuccess: () => setSelectedRowKeys([]),
            });
          }}
          searchText={searchText}
          setSearchText={(value) => {
            setShowLoader(true);
            setSearchText(value);
            setTimeout(() => setShowLoader(false), 500);
          }}
        />

        <div
          className={`card p-5 rounded-lg paddingTop-0 ${
            storeTotalCount <= process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE
              ? "paddingBottom-0"
              : "minHeight-100vh"
          }`}
        >
          <div className="table-area">
            <div className="table-responsive">
              <div className="tab-content" id="alltable-size">
                <Form form={tableForm} component={false}>
                  <TableContent
                    bordered={false}
                    columns={mergedColumns}
                    rowClassName="editable-row"
                    rowSelection={
                      storeRole === process.env.REACT_APP_SROLE && rowSelection
                    }
                    storeList={filteredCurrencies}
                    components={{
                      body: {
                        cell: EditableCell,
                      },
                    }}
                    pagination={storePageInfo}
                    loading={showLoader}
                    totalCount={storeTotalCount}
                    handlePaginationChange={(page, pageSize) =>
                      deFaultHandlePaginationChange(
                        (type, pagination) =>
                          dispatch(
                            getCurrencyListThunk({
                              route: currentPath,
                              pagination,
                            })
                          ),
                        page,
                        pageSize,
                        storeTotalCount
                      )
                    }
                  />
                </Form>
              </div>
            </div>
          </div>
        </div>
        <EditForm
          title="Edit Currency"
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          form={tableForm}
          fields={[
            {
              label: "Currency Code",
              name: "code",
              rules: [
                { required: true, message: "Please input currency code!" },
              ],

              type: "input",
            },
            {
              label: "Currency Symbol",
              name: "symbol_native",
              type: "input",
            },
            {
              label: "Currency Rate History",
              name: "currencyRateHistory",
              type: "custom",
              render: (form, value, onChange) => (
                <CurrencyRateHistoryList
                  name="currencyRateHistory"
                  value={value}
                  onChange={onChange}
                  role={storeRole}
                  disabledDate= {(current) => {        
                    return storeRole !== process.env.REACT_APP_SROLE && current && current < moment().startOf("month");
                  }}
                />
              ),
            },
          ]}
          submitThunk={editCurrencyThunk}
          payloadBuilder={(values) => ({
            id: editingCurrency.key,
            code: values?.code,
            symbol_native: values?.symbol_native,
            currencyRateHistory:
              values.currencyRateHistory?.map((item) => {
                const raw = item?.effectiveDateTime;

                // If it's already a dayjs object, use it; otherwise try to build one
                const maybeDayjs = dayjs.isDayjs(raw) ? raw : dayjs(raw);

                return {
                  ...item,
                  effectiveDateTime:
                    maybeDayjs && maybeDayjs.isValid()
                      ? maybeDayjs.format("YYYY-MM-DD HH:mm")
                      : null,
                };
              }) ?? [],
            historySource: "backend",
            historyAction: "update",
            historyUserID: storeLIUId,
          })}
          successMessage="Currency updated successfully."
          currentPath={currentPath}
          refreshListThunk={getCurrencyListThunk}
        />
      </div>
    </div>
  );
}
